import React, { useEffect, useMemo, useRef, useState } from 'react';
import styles from './ZoomVideoEditor.module.scss';
import { useTranslation } from 'react-i18next';
import CloseButton from '../../../../components/CloseButton/CloseButton';
import { useZoomContext } from '../../../../context/ZoomContext';
import { CustomButton } from '../../../../components/Button/Button';
import Slider from 'rc-slider';
import { v4 as uuidv4 } from 'uuid';
import Icon from '../../../../components/Icon/Icon';
import { ToastNotification } from '../../../../components/Toast/Toast';
import ZoomTypeItem from '../ZoomTypeItem/ZoomTypeItem';
import { ZOOM_TYPES } from '../../constants';

const MIN_SCALE = 1;
const MAX_SCALE = 4;

const MIN_ZOOM_DURATION = 1000; // value in ms
const DEFAULT_ZOOM_DURATION = 5000; // value in ms

const DEFAULT_MARGIN_X = 50; // value in percents
const DEFAULT_MARGIN_Y = 50; // value in percents

const ZoomVideoEditor = ({ activeScene, currentVideoPart, videoDuration, handleZoomPointsChange, miniSidebar, closeFeatures }) => {
  const {
    disableZoom,
    zoomProgress,
    zooms,
    initialZooms,
    selectedZoom,
    addNewZoom,
    isZoomsChanged,
    changeZooms,
    changeSelectedZoom,
    zoomChanged,
    updateZoomsSeekValue,
  } = useZoomContext();

  const [isDragActive, setIsDragActive] = useState(false);
  const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });

  const canvasRef = useRef();
  const draggableAreaRef = useRef();
  const currentVideoRef = useRef(null);

  const { t } = useTranslation();

  const playbackRate = useMemo(() => activeScene?.playbackRate || 1, [activeScene]);

  const zoomsRange = useMemo(() => zooms.map(({ id, offset, duration }) => ({ id, startTime: offset, endTime: offset + duration })), [
    zooms,
  ]);

  const insideZoom = useMemo(() => {
    const currentTime = zoomProgress.milliseconds ? zoomProgress.milliseconds - activeScene?.trimStart : activeScene?.trimStart;
    return zoomsRange.find(({ startTime, endTime }) => startTime <= currentTime && currentTime < endTime);
  }, [zoomProgress, videoDuration, zoomsRange]);

  const availableDurationBeforeEnd = useMemo(() => {
    const currentTime = zoomProgress.milliseconds ? zoomProgress.milliseconds - activeScene?.trimStart : activeScene?.trimStart;

    // If the timestamp is inside the zoom, then the new zoom will be added
    // immediately after the current zoom if there is a minimum zoom duration available
    if (insideZoom) {
      const isLastZoom = zoomsRange.some(({ id }, index) => id === insideZoom?.id && index === zoomsRange?.length - 1);
      if (isLastZoom) {
        if (videoDuration - insideZoom.endTime < MIN_ZOOM_DURATION / playbackRate) {
          return null;
        }

        if (videoDuration - insideZoom.endTime < DEFAULT_ZOOM_DURATION / playbackRate) {
          return MIN_ZOOM_DURATION / playbackRate;
        }

        return DEFAULT_ZOOM_DURATION / playbackRate;
      }
    }

    if (videoDuration - currentTime < MIN_ZOOM_DURATION / playbackRate) {
      return null;
    }

    if (videoDuration - currentTime < DEFAULT_ZOOM_DURATION / playbackRate) {
      return MIN_ZOOM_DURATION / playbackRate;
    }

    return DEFAULT_ZOOM_DURATION / playbackRate;
  }, [zoomProgress, videoDuration, zoomsRange, insideZoom]);

  const availableDurationBeforeNextZoom = useMemo(() => {
    const currentTime = zoomProgress.milliseconds ? zoomProgress.milliseconds - activeScene?.trimStart : activeScene?.trimStart;
    const isInvalidTimestamps = zoomsRange.some(({ id, startTime, endTime }, index) => {
      // If the timestamp is inside the zoom, then the new zoom will be added
      // immediately after the current zoom if there is a minimum zoom duration available
      if (insideZoom?.id === id) {
        const nextZoom = zoomsRange[index + 1];
        if (nextZoom) {
          // After current zoom
          const isShorterThanMinimum = nextZoom.startTime - endTime < MIN_ZOOM_DURATION / playbackRate;
          return isShorterThanMinimum;
        }
      }

      const isShorterThanMinimum = startTime > currentTime && startTime - currentTime < MIN_ZOOM_DURATION / playbackRate;
      return isShorterThanMinimum;
    });

    if (isInvalidTimestamps) {
      return null;
    }

    const isShorterThanDefault = zoomsRange.some(({ id, startTime, endTime }, index) => {
      if (insideZoom?.id === id) {
        const nextZoom = zoomsRange[index + 1];
        if (nextZoom) {
          return nextZoom.startTime - endTime < DEFAULT_ZOOM_DURATION / playbackRate;
        }
      }

      return startTime > currentTime && startTime - currentTime < DEFAULT_ZOOM_DURATION / playbackRate;
    });

    if (isShorterThanDefault) {
      return MIN_ZOOM_DURATION / playbackRate;
    }

    return DEFAULT_ZOOM_DURATION / playbackRate;
  }, [zoomProgress, videoDuration, zoomsRange, insideZoom]);

  const draggableAreaSize = useMemo(() => {
    if (canvasRef.current) {
      const { height, width } = canvasSize;

      return {
        width,
        height,
      };
    }
    return {};
  }, [canvasSize.height, canvasSize.width]);

  const handleChangeZoomValue = scale => {
    changeZooms(selectedZoom?.id, { scale });
    changeSelectedZoom({
      ...selectedZoom,
      scale,
    });

    zoomChanged(true);
  };

  const handleChangeZoomType = type => {
    changeZooms(selectedZoom?.id, { zoomType: type });
    changeSelectedZoom({
      ...selectedZoom,
      zoomType: type,
    });

    zoomChanged(true);
  };

  const handleChangeZoomPosition = (marginX, marginY) => {
    changeZooms(selectedZoom?.id, { marginX, marginY });
    changeSelectedZoom({
      ...selectedZoom,
      marginX,
      marginY,
    });

    zoomChanged(true);
  };

  const handleStartDrag = () => {
    setIsDragActive(true);
  };

  const handleMouseUp = () => {
    setIsDragActive(false);
  };

  const handleMove = event => {
    if (!isDragActive || !draggableAreaRef.current) {
      return;
    }

    // Сalculate the indent as a percentage from the top left edge
    const areaSize = draggableAreaRef.current.getBoundingClientRect();
    let marginX = ((event.clientX - areaSize.left) / areaSize.width) * 100;
    let marginY = ((event.clientY - areaSize.top) / areaSize.height) * 100;

    if (marginX < 0) {
      marginX = 0;
    }

    if (marginX > 100) {
      marginX = 100;
    }

    if (marginY < 0) {
      marginY = 0;
    }

    if (marginY > 100) {
      marginY = 100;
    }

    handleChangeZoomPosition(Math.round(marginX), Math.round(marginY));
  };

  const handleAddNewZoom = () => {
    if (!availableDurationBeforeNextZoom || !availableDurationBeforeEnd) {
      ToastNotification({ message: t('zoom.editor.new_action_tooltip.incorrect_position') });
      return;
    }

    // Need take into account trimStart and playbackRate values since zoomProgress.milliseconds is a value from a native player that store the whole duration
    const offset =
      (zoomProgress.milliseconds ? zoomProgress.milliseconds - activeScene?.trimStart : activeScene?.trimStart) /
      (activeScene?.playbackRate || 1);

    const isLastZoom = zooms.every(zoom => offset >= zoom.offset);
    const duration = isLastZoom ? availableDurationBeforeEnd : availableDurationBeforeNextZoom;
    const finalOffset = Math.floor(insideZoom ? insideZoom.endTime : offset);

    // If the timestamp is inside the zoom, then the new zoom will be added
    // immediately after the current zoom if there is a minimum zoom duration available
    const newZoom = {
      id: uuidv4(),
      scale: MIN_SCALE,
      offset: finalOffset,
      duration,
      marginX: DEFAULT_MARGIN_X,
      marginY: DEFAULT_MARGIN_Y,
      zoomType: ZOOM_TYPES.SOFT.type,
    };

    addNewZoom(newZoom);
    zoomChanged(true);

    if (insideZoom) {
      // We move to the beginning of the new added zoom so that we can add another new zoom immediately
      const stepFromStartOfZoom = 1; // value in ms
      updateZoomsSeekValue((finalOffset + stepFromStartOfZoom) / videoDuration);
    }
  };

  const handleClose = () => {
    disableZoom();

    // we changed the zoom values based on the current scene settings (scene start and scene speed) and these values were mutated
    // So we need to convert back to real values.
    const realInitialZooms = initialZooms?.map(initialZoom => {
      const realOffset = initialZoom.offset * playbackRate + activeScene?.trimStart;
      const realDuration = initialZoom.duration * playbackRate;

      return {
        ...initialZoom,
        offset: realOffset,
        duration: realDuration,
      };
    });

    handleZoomPointsChange({
      sceneIndex: Number(currentVideoPart),
      zoomPoints: realInitialZooms,
    });
  };

  const handleSaveChanges = () => {
    // We changed the zoom values based on the current scene settings (scene start and scene speed) and these values were mutated
    // So we need to convert back to real values.
    const realZooms = zooms?.map(zoom => {
      const realOffset = zoom.offset * playbackRate + activeScene?.trimStart;
      const realDuration = zoom.duration * playbackRate;

      return {
        ...zoom,
        offset: realOffset,
        duration: realDuration,
      };
    });

    const realInitialZooms = initialZooms?.map(initialZoom => {
      const realOffset = initialZoom.offset * playbackRate + activeScene?.trimStart;
      const realDuration = initialZoom.duration * playbackRate;

      return {
        ...initialZoom,
        offset: realOffset,
        duration: realDuration,
      };
    });

    handleZoomPointsChange({
      sceneIndex: Number(currentVideoPart),
      zoomPoints: realZooms,
      initialZoomPoints: realInitialZooms,
      registerAction: true,
    });
    disableZoom();
  };

  useEffect(() => {
    const container = document.getElementById(activeScene?.id);
    const [currentVideo] = container?.getElementsByTagName('video') || [];
    if (currentVideo) {
      currentVideoRef.current = currentVideo;
    }

    // draw thumbnail until video is loaded
    if (!canvasRef.current) {
      return;
    }

    const canvasContext = canvasRef.current.getContext('2d');
    canvasContext.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);

    canvasRef.current.width = activeScene?.dimensions?.width;
    canvasRef.current.height = activeScene?.dimensions?.height;

    const thumbnailImage = new Image();
    thumbnailImage.onload = () => canvasContext.drawImage(thumbnailImage, 0, 0);
    thumbnailImage.src = activeScene?.thumbnail;

    setCanvasSize({
      width: canvasRef.current?.clientWidth,
      height: canvasRef.current?.clientHeight,
    });
  }, [activeScene?.id]);

  useEffect(() => {
    if (currentVideoRef.current) {
      const getCurrentImage = () => {
        if (!canvasRef.current) {
          return;
        }

        const videoWidth = currentVideoRef.current.videoWidth;
        const videoHeight = currentVideoRef.current.videoHeight;

        // Take the current frame from the video only if the video has height and width (content is loaded)
        // Otherwise thumbnail will be shown
        if (videoWidth && videoHeight) {
          const canvasContext = canvasRef.current.getContext('2d');
          canvasContext.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);

          canvasRef.current.width = videoWidth;
          canvasRef.current.height = videoHeight;

          canvasContext.drawImage(currentVideoRef.current, 0, 0);
          setCanvasSize({
            width: canvasRef.current?.clientWidth,
            height: canvasRef.current?.clientHeight,
          });
        }

        currentVideoRef.current.requestVideoFrameCallback(getCurrentImage);
      };

      getCurrentImage();
    }
  }, [currentVideoRef.current]);

  useEffect(() => {
    if (miniSidebar || closeFeatures) {
      handleClose();
    }
  }, [miniSidebar, closeFeatures]);

  useEffect(() => {
    if (isDragActive) {
      document.addEventListener('mousemove', handleMove);
    }

    return () => document.removeEventListener('mousemove', handleMove);
  }, [isDragActive]);

  useEffect(() => {
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      disableZoom();
      zoomChanged(false);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);

  return (
    <div className={styles.wrapper}>
      <div className={styles.mainContainer}>
        <div className={styles.headerContainer}>
          <h2 className={styles.editorHeader}>{t('my_video.tools.zoom.title')}</h2>
          <CloseButton onClose={handleClose} />
        </div>
        <div className={styles.canvasContainer}>
          <div className={styles.canvasWrapper}>
            <canvas ref={canvasRef} className={styles.canvasImage} />
          </div>
          <div className={styles.draggableArea} ref={draggableAreaRef} style={draggableAreaSize}>
            {selectedZoom?.id && !!draggableAreaSize.width && (
              <Icon
                src="zoomPoint"
                className={styles.zoomPointIcon}
                onMouseDown={handleStartDrag}
                style={{ left: `${selectedZoom?.marginX}%`, top: `${selectedZoom?.marginY}%` }}
              />
            )}
          </div>
        </div>
        <CustomButton
          label={t('my_video.tools.zoom.add_zoom')}
          variant="secondary"
          onClick={handleAddNewZoom}
          className={styles.saveBtn}
        />
        {selectedZoom?.id && (
          <div className={styles.optionContainer}>
            <h3 className={styles.subHeader}>{t('my_video.tools.zoom.scale')}</h3>
            <div className={styles.sliderContainer}>
              <span className={styles.minMaxValue}>{MIN_SCALE}</span>
              <Slider
                min={MIN_SCALE}
                max={MAX_SCALE}
                step={0.125}
                onChange={value => handleChangeZoomValue(value)}
                value={selectedZoom?.scale || MIN_SCALE}
                dots={true}
                included
                style={{ width: '350px' }}
                handleStyle={{
                  width: '18px',
                  height: '18px',
                  background: '#FFFFFF',
                  position: 'relative',
                  top: '-10px',
                  borderRadius: '9px',
                  boxShadow: '0px 0px 10px rgba(0, 0, 0, 0.3)',
                  cursor: 'pointer',
                }}
                trackStyle={{
                  background: '#37C1A0',
                  height: '4px',
                  borderRadius: '2px',
                  position: 'relative',
                  top: '-4px',
                }}
                railStyle={{
                  background: '#EEEEEE',
                  height: '4px',
                  borderRadius: '2px',
                  position: 'relative',
                }}
                dotStyle={{
                  marginLeft: '2px',
                  color: '#37c1a0',
                  bottom: '6px',
                }}
                activeDotStyle={{
                  borderColor: '#37c1a0',
                  backgroundColor: '#37c1a0',
                }}
              />
              <span className={styles.minMaxValue}>{MAX_SCALE}</span>
              <div className={styles.currentValueContainer}>
                <div
                  className={styles.currentValue}
                  style={{
                    left: `calc(${((selectedZoom?.scale - MIN_SCALE) / (MAX_SCALE - MIN_SCALE)) * 100}%)`,
                  }}
                >
                  <div className={styles.triangleUp} />
                  {selectedZoom?.scale || MIN_SCALE}
                </div>
              </div>
            </div>
          </div>
        )}
        {selectedZoom?.id && (
          <div className={styles.optionContainer}>
            <h3 className={styles.subHeader}>{t('my_video.tools.zoom.type')}</h3>
            <div className={styles.typesList}>
              {Object.values(ZOOM_TYPES).map(({ tooltip, type }) => (
                <ZoomTypeItem
                  key={type}
                  type={type}
                  tooltip={tooltip}
                  active={type === (selectedZoom.zoomType ?? ZOOM_TYPES.SOFT.type)}
                  onClick={type => handleChangeZoomType(type)}
                />
              ))}
            </div>
          </div>
        )}
      </div>
      <div className={styles.btnContainer}>
        <CustomButton
          onClick={handleSaveChanges}
          disabled={!isZoomsChanged}
          className={styles.saveBtn}
          label={t('general.save')}
          variant="primary"
        />
      </div>
    </div>
  );
};

export default ZoomVideoEditor;
